<i18n src="./resources/locales.json"></i18n>
<template>
  <div class="sms-settings-side-panel">
    <v-list class="sms-settings-items custom-scrollbar">
      <!-- User Information -->
      <h5 class="sms-settings-header px-4 my-2">{{ $t('sidePanel.settings.headers.currentUser') }}</h5>
      <SmsContactItem :contact="currentUser" class="sms-default-cursor" />
      <v-list-item class="text-center">
        <v-btn class="sms-primary-button" @click="logout" block>
          <v-icon left>mdi-logout</v-icon>
          {{ $t('sidePanel.settings.signOut').toUpperCase() }}
        </v-btn>
      </v-list-item>
      <br />

      <!-- Display Preferences -->
      <h5 id="sms-settings-display" class="sms-settings-header px-4 mb-2">
        {{ $t('sidePanel.settings.headers.display') }}
      </h5>
      <v-list-item>
        <v-select
          :items="getAllThemes"
          :label="$t('sidePanel.settings.themesLabel')"
          item-text="label"
          item-value="name"
          v-model="selectedTheme"
        >
        </v-select>
      </v-list-item>
      <v-list-item>
        <v-select
          :items="languages"
          :label="$t('sidePanel.settings.locationLabel')"
          item-text="i18n_key"
          item-value="value"
          v-model="generalSettings.displayPreferences.language"
          @change="saveSettings('language')"
        ></v-select>
      </v-list-item>
      <v-list-item>
        <v-select
          :items="timeZones"
          :label="$t('sidePanel.settings.timeZoneLabel')"
          item-text="text"
          item-value="value"
          v-model="generalSettings.displayPreferences.timeZone"
          @change="saveSettings('timeZone')"
        ></v-select>
      </v-list-item>
      <v-list-item>
        <v-select
          :items="timeFormats"
          :label="$t('sidePanel.settings.timeFormatLabel')"
          item-text="key"
          item-value="value"
          v-model="generalSettings.displayPreferences.timeFormat"
          @change="saveSettings('timeFormat')"
        ></v-select>
      </v-list-item>
      <br />

      <!-- Additional Options -->
      <h5 id="sms-settings-additional" class="sms-settings-header px-4 mb-2">
        {{ $t('sidePanel.settings.headers.additionalOptions') }}
      </h5>
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title>{{ $t('sidePanel.settings.enableNotifications') }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-switch
            class="sms-settings-notifications-switch"
            v-model="generalSettings.additionalOptions.enableNotifications"
            @change="saveSettings('enableNotifications')"
          />
        </v-list-item-action>
      </v-list-item>
      <v-list-item>
        <v-select
          :items="incomingNotificationSounds"
          :label="$t('sidePanel.settings.notificationSounds')"
          v-model="generalSettings.additionalOptions.notificationSound"
          @change="value => changeSelectedNotificationSound(value)"
        >
          <template slot="selection" slot-scope="data">
            <span>{{ data.item.i18nKey ? $t(data.item.i18nKey) : data.item.label }}</span>
          </template>
          <template slot="item" slot-scope="data">
            <span>{{ data.item.i18nKey ? $t(data.item.i18nKey) : data.item.label }}</span>
          </template>
        </v-select>
        <v-list-item-action v-if="selectedIncomingNotificationSound && selectedIncomingNotificationSound.buffer">
          <v-btn icon @click="playNotificationSound()">
            <v-icon button>mdi-volume-high</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>

      <v-list-item @click="downloadConversations">
        <v-list-item-content>
          <v-list-item-title>{{ $t('sidePanel.settings.downloadSmsMessagesBtn') }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-icon left>mdi-open-in-new</v-icon>
        </v-list-item-action>
      </v-list-item>
      <br />

      <!-- Application Information -->
      <h5 class="sms-settings-header px-4 mb-2">{{ $t('sidePanel.settings.headers.appInfo') }}</h5>
      <v-list-item @click="viewReleaseNotes">
        <v-list-item-content>
          <v-list-item-title>{{ this.applicationInfo.title }}</v-list-item-title>
          <v-list-item-subtitle>{{ this.applicationInfo.version }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-icon>mdi-information-outline</v-icon>
        </v-list-item-action>
      </v-list-item>
      <v-list-item @click="launchSmsTour">
        <v-list-item-content>
          <v-list-item-title>{{ $t('sidePanel.settings.launchTour') }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-icon>mdi-map-outline</v-icon>
        </v-list-item-action>
      </v-list-item>

      <v-divider></v-divider>

      <v-list-item @click="showApplicationFeedback">
        <v-list-item-content>
          <v-list-item-title>{{ $t('sidePanel.settings.applicationFeedback') }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-icon>mdi-comment-quote-outline</v-icon>
        </v-list-item-action>
      </v-list-item>

      <v-divider></v-divider>

      <v-subheader>{{ this.applicationInfo.copy }}</v-subheader>
    </v-list>

    <SmsConversationDownloaderDialog ref="conversationDownloaderDialog" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SmsContactItem from '@/components/SmsContactItem/SmsContactItem';
import SmsConversationDownloaderDialog from '@/components/SmsConversation/SmsConversationDownloaderDialog/SmsConversationDownloaderDialog';
import ValidationMixin from '@/mixins/ValidationMixin';
import { TAB_NAME } from '@/constants/tabNameConstants';

export default {
  name: 'SmsSettingsSidePanel',

  components: {
    SmsConversationDownloaderDialog,
    SmsContactItem
  },

  mixins: [ValidationMixin],

  data() {
    return {
      notificationSoundAudioBuffer: { context: null, sourceNode: null },
      generalSettings: {},
      timeZones: []
    };
  },

  computed: {
    ...mapGetters('user', ['currentUser', 'activeNumber']),
    ...mapGetters('themes', ['allThemes', 'selectedThemeName']),
    ...mapGetters('preferences', [
      'hasNotificationsEnable',
      'incomingNotificationSounds',
      'selectedIncomingNotificationSound',
      'getSettings',
      'userLocale'
    ]),

    applicationInfo() {
      // There is an open issue in i18n that can not be used by referring inside objects.
      // https://github.com/kazupon/vue-i18n/issues/259
      const translate = key => this.$t(key);
      return {
        title: translate('applicationInfo.currentVersionLabel'),
        version: process.env.APPLICATION_VERSION,
        copy: translate('applicationInfo.copyrightNotes')
      };
    },

    selectedTheme: {
      get() {
        return this.generalSettings.displayPreferences.theme;
      },
      set(newThemeName) {
        this.changeThemeByName({
          name: newThemeName,
          vuetify: this.$vuetify
        });
        this.generalSettings.displayPreferences.theme = newThemeName;
        this.saveSettings('theme');
      }
    },

    languages() {
      return [
        {
          i18n_key: this.$t('sidePanel.settings.languageEnValue'),
          value: 'en'
        },
        {
          i18n_key: this.$t('sidePanel.settings.languageEsValue'),
          value: 'es'
        }
      ]
    },

    timeFormats() {
      return [
        { key: this.$t('sidePanel.settings.twelveFormat'), value: 'h:mm A' },
        { key: this.$t('sidePanel.settings.twentyFourFormat'), value: 'HH:mm' }
      ]
    },

    getAllThemes() {
      const themes = this.allThemes.map(theme => {
        return {
          label: this.$t(`sidePanel.settings.${theme.name}`),
          name: theme.name
        }
      });
      return themes;
    }
  },

  methods: {
    ...mapActions('themes', ['changeThemeByName']),
    ...mapActions('preferences', ['setIncomingNotificationSound', 'updateSettings']),

    downloadConversations() {
      return this.$refs.conversationDownloaderDialog.open({ messagesMode: true }).catch(console.log);
    },
    changeSelectedNotificationSound(newNotificationSoundSelected) {
      const newSelectedIncomingNotificationSound = this.incomingNotificationSounds.find(
        notificationSound => notificationSound.value === newNotificationSoundSelected
      );
      this.setIncomingNotificationSound(newSelectedIncomingNotificationSound);
      let currentUserSettings = this.getSettings;
      currentUserSettings.additionalOptions.notificationSound = this.generalSettings.additionalOptions.notificationSound;
      currentUserSettings.interfaceState.currentTab = TAB_NAME.settings;
      this.updateSettings(this.generalSettings);
    },
    playNotificationSound() {
      this.$notificationSoundManager.playNotificationSound(
        this.selectedIncomingNotificationSound,
        this.notificationSoundAudioBuffer
      );
    },
    saveSettings(settingId) {
      let currentUserSettings = this.getSettings;
      currentUserSettings.displayPreferences[settingId] = this.generalSettings.displayPreferences[settingId];
      currentUserSettings.interfaceState.currentTab = TAB_NAME.settings;
      this.updateSettings(this.generalSettings);
      this.$root.$i18n.locale = this.userLocale;
      this.$root.$moment.locale(this.userLocale);
      this.$root.$moment.tz(this.generalSettings.displayPreferences.timeZone);
    },
    launchSmsTour() {
      this.$root.$emit('launch-sms-tour');
    },
    loadTimeZones() {
      const recommendedTimeZone = this.$moment.tz.guess();
      const americanZones = this.$moment.tz.zonesForCountry('US');
      const canadianZones = this.$moment.tz.zonesForCountry('CA');

      const convertToListObject = (timezone, country) => {
        let prefix = '';
        if (!country) {
          if (americanZones.includes(timezone)) {
            prefix = '[US]';
          } else if (canadianZones.includes(timezone)) {
            prefix = '[CAN]';
          }
        } else {
          if (country === 'US') {
            prefix = '[US]';
          } else if (country === 'CA') {
            prefix = '[CAN]';
          }
        }
        let name = timezone
          .replace('America/', '')
          .replace('Pacific/', '')
          .replace(/_/g, ' ');
        return {
          text: `${prefix} ${name} (${this.$moment.tz(timezone).format('z Z')})`,
          value: timezone
        };
      };

      this.timeZones.push(convertToListObject(recommendedTimeZone));
      this.timeZones.push({ divider: true });
      this.timeZones.push(...americanZones.map(z => convertToListObject(z, 'US')));
      this.timeZones.push(...canadianZones.map(z => convertToListObject(z, 'CA')));
    },
    logout() {
      this.$root.$emit('close-session');
    },
    viewReleaseNotes() {
      // TODO: Open dialog with Release notes summary from Confluence API, include actions: Close, Full Release Notes
    },
    showApplicationFeedback() {
      window.open('https://evolvesms.featureupvote.com/?order=popular&filter=allexceptdone');
    }
  },

  created() {
    this.generalSettings = this.getSettings;
  },

  async mounted() {
    this.loadTimeZones();
  },
};
</script>

<style scoped>
.sms-settings-notifications {
  position: relative;
  height: 50px;
}
.sms-settings-notifications-label {
  float: left;
  width: calc(100% - 46px);
  text-align: left;
}

.sms-settings-notifications-switch {
  float: right;
  padding: 0px;
  margin: 0px;
}

.card-content {
  margin-top: 10px;
}
.label-text {
  text-transform: capitalize;
}
</style>

<style>
.sms-settings-header {
  display: block;
  color: var(--v-primary-base);
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.sms-settings-side-panel {
  height: 100%;
  display: grid;
}

.sms-settings-side-panel .title,
.sms-settings-side-panel .sub-title {
  max-width: 270px;
  text-align: left;
}

.sms-settings-side-panel .v-btn__content {
  max-width: 260px;
  display: inline;
  text-align: center;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.sms-settings-side-panel .v-text-field .v-label--active {
  top: -1px !important;
}

.sms-default-cursor {
  cursor: default !important;
}

.sms-settings-items {
  overflow: auto;
  background: var(--v-surface-base) !important;
}
</style>
